import { confirmDialog } from '../ui/ConfirmDialog';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { clsx } from 'clsx';
import { FileText, Image as ImageIcon, File, Download, Trash2, Upload, Loader2 } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { uploadPropertyDocument, getPropertyDocumentDownloadUrl } from '../../lib/property-documents';

// Property documents — list + upload + download for the current property.
// Backed by:
//   - GET list:        SELECT FROM property_documents (RLS-gated)
//   - Upload:          uploadPropertyDocument helper (presign → R2 PUT → RPC)
//   - Download:        POST /functions/v1/document-presign-get → window.open
//   - Soft-delete:     UPDATE property_documents SET is_deleted = true (RLS-gated)
//
// RLS read: assigned agent + agency_admin (same agency) + super_admin + same-agency
// agent with can_view_all_properties (read-only). See migrations
// 20260504040000_property_documents_rls.sql and 20260514140000_property_documents_rls_view_all.sql.
// RLS write: assigned agent + agency_admin (same agency) + super_admin. The caller
// gates the UI for writes via the `canWrite` prop — view-all agents pass false so
// Upload/Delete affordances stay hidden.

interface Doc {
  id: string;
  name: string;
  storage_path: string;
  size_bytes: number | null;
  mime_type: string | null;
  created_at: string;
  legacy_id: number | null;
}

interface Props {
  propertyId: string;
  theme: 'light' | 'dark' | 'high-contrast';
  canWrite: boolean;
}

function formatSize(bytes: number | null): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function pickIcon(mime: string | null) {
  if (!mime) return File;
  if (mime === 'application/pdf') return FileText;
  if (mime.startsWith('image/')) return ImageIcon;
  if (mime.includes('word') || mime.includes('excel') || mime.includes('spreadsheet') || mime.includes('document')) return FileText;
  return File;
}

export const PropertyDocumentsSection: React.FC<Props> = ({ propertyId, theme, canWrite }) => {
  const isLight = theme !== 'dark';
  const panelCls = clsx(
    'p-6 rounded-2xl border',
    isLight ? 'bg-white border-slate-100 shadow-sm' : 'glass-panel border-white/5',
  );

  const [docs, setDocs] = useState<Doc[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    const { data, error: err } = await supabase
      .from('property_documents')
      .select('id, name, storage_path, size_bytes, mime_type, created_at, legacy_id')
      .eq('property_id', propertyId)
      .eq('is_deleted', false)
      .order('created_at', { ascending: false });
    if (err) {
      setError(err.message);
      setDocs([]);
    } else {
      setDocs(data ?? []);
      setError(null);
    }
    setLoading(false);
  }, [propertyId]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const handlePick = () => fileInputRef.current?.click();

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow picking same file twice
    if (!file) return;

    setError(null);
    setUploading(true);
    const result = await uploadPropertyDocument(propertyId, file);
    if (!result.ok) {
      setError(result.error ?? 'Error al subir el documento');
    } else {
      await fetchDocs();
    }
    setUploading(false);
  };

  const handleDownload = async (doc: Doc) => {
    setDownloadingId(doc.id);
    const result = await getPropertyDocumentDownloadUrl(doc.id);
    if (result.ok && result.downloadUrl) {
      window.open(result.downloadUrl, '_blank', 'noopener');
    } else {
      setError(result.error ?? 'Error al descargar');
    }
    setDownloadingId(null);
  };

  const handleDelete = async (doc: Doc) => {
    if (!await confirmDialog(`¿Eliminar "${doc.name}"? El archivo permanecerá en almacenamiento pero ya no aparecerá en la lista.`)) return;
    setDeletingId(doc.id);
    const { error: err } = await supabase
      .from('property_documents')
      .update({ is_deleted: true, deleted_at: new Date().toISOString() })
      .eq('id', doc.id);
    if (err) {
      setError(err.message);
    } else {
      setDocs(prev => (prev ?? []).filter(d => d.id !== doc.id));
    }
    setDeletingId(null);
  };

  return (
    <div className={panelCls}>
      <div className="flex items-center justify-between mb-4">
        <h3 className={clsx('text-sm font-black uppercase tracking-widest', isLight ? 'text-slate-700' : 'text-white')}>
          Documentos
        </h3>
        {canWrite && (
          <>
            <button
              onClick={handlePick}
              disabled={uploading}
              className={clsx(
                'flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition-all',
                'bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed',
              )}
            >
              {uploading
                ? <><Loader2 size={14} className="animate-spin" /> Subiendo…</>
                : <><Upload size={14} /> Subir documento</>}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,image/jpeg,image/png,image/webp,image/gif,.doc,.docx,.xls,.xlsx,.txt"
              onChange={handleUpload}
            />
          </>
        )}
      </div>

      {error && (
        <div className={clsx(
          'mb-3 p-2.5 rounded-lg text-xs font-medium',
          isLight ? 'bg-rose-50 text-rose-700 border border-rose-200' : 'bg-rose-500/10 text-rose-300 border border-rose-500/20',
        )}>
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 size={20} className="animate-spin text-slate-400" />
        </div>
      ) : (docs && docs.length > 0) ? (
        <ul className="space-y-2">
          {docs.map(doc => {
            const Icon = pickIcon(doc.mime_type);
            const isLegacy = doc.legacy_id != null;
            return (
              <li
                key={doc.id}
                className={clsx(
                  'flex items-center gap-3 p-3 rounded-xl transition-colors',
                  isLight ? 'bg-slate-50 hover:bg-slate-100' : 'bg-white/5 hover:bg-white/10',
                )}
              >
                <div className={clsx(
                  'shrink-0 w-10 h-10 rounded-lg flex items-center justify-center',
                  doc.mime_type === 'application/pdf'
                    ? (isLight ? 'bg-rose-100 text-rose-600' : 'bg-rose-500/20 text-rose-400')
                    : doc.mime_type?.startsWith('image/')
                    ? (isLight ? 'bg-emerald-100 text-emerald-600' : 'bg-emerald-500/20 text-emerald-400')
                    : (isLight ? 'bg-slate-200 text-slate-500' : 'bg-white/10 text-slate-400'),
                )}>
                  <Icon size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={clsx('text-sm font-semibold truncate', isLight ? 'text-slate-900' : 'text-white')}>
                    {doc.name}
                  </p>
                  <p className={clsx('text-xs', isLight ? 'text-slate-500' : 'text-slate-400')}>
                    {formatSize(doc.size_bytes)} · {new Date(doc.created_at).toLocaleDateString('es-GT')}
                    {isLegacy && <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 text-[10px] font-bold uppercase tracking-wider">Legado</span>}
                  </p>
                </div>
                <button
                  onClick={() => handleDownload(doc)}
                  disabled={downloadingId === doc.id}
                  title="Ver / Descargar"
                  className={clsx(
                    'shrink-0 p-2 rounded-lg transition-colors',
                    isLight ? 'text-slate-600 hover:bg-slate-200' : 'text-slate-300 hover:bg-white/10',
                    'disabled:opacity-50',
                  )}
                >
                  {downloadingId === doc.id ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
                </button>
                {canWrite && (
                  <button
                    onClick={() => handleDelete(doc)}
                    disabled={deletingId === doc.id}
                    title="Eliminar"
                    className={clsx(
                      'shrink-0 p-2 rounded-lg transition-colors',
                      isLight ? 'text-rose-500 hover:bg-rose-50' : 'text-rose-400 hover:bg-rose-500/10',
                      'disabled:opacity-50',
                    )}
                  >
                    {deletingId === doc.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className={clsx('text-sm py-4 text-center', isLight ? 'text-slate-500' : 'text-slate-400')}>
          Aún no hay documentos para esta propiedad.
        </p>
      )}
    </div>
  );
};
